// Single source of truth for Analytics datetime formatting.
//
// All Analytics surfaces (per-day strip, drill-down, leaderboards, gate-closure
// labels) must format times in the resource timezone so the user is never
// asked to mentally translate UTC ↔ local. The resource timezone is
// hardcoded to Europe/Brussels for now — all current resources are Belgian.
// When per-resource timezones land, change `getResourceTimezone()` to read
// from the selected resource and pass it through these helpers.

export const RESOURCE_TIMEZONE = 'Europe/Brussels';

export const getResourceTimezone = () => RESOURCE_TIMEZONE;

const toDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // Date-only strings ("2025-04-15") are parsed as UTC midnight by JS, which
  // matches the backend's DateField semantics — the day is calendrical, not
  // an instant. Datetime strings keep their offset.
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const partsInTz = (date, tz, options) => {
  const fmt = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: tz });
  return Object.fromEntries(
    fmt.formatToParts(date).map(({ type, value }) => [type, value])
  );
};

/**
 * Day-of-month (1–31) for the given instant in the resource timezone.
 * Use this anywhere you previously called `.getUTCDate()` against
 * `target_day`-style values.
 */
export const getDayInResourceTz = (value, tz = RESOURCE_TIMEZONE) => {
  const d = toDate(value);
  if (!d) return null;
  const { day } = partsInTz(d, tz, { day: '2-digit' });
  return Number(day);
};

/**
 * "HH:mm" in the resource timezone. Used by the per-day drill-down so
 * timestamp columns and gate-closure rows match the rest of Analytics.
 */
export const getHourMinuteInResourceTz = (value, tz = RESOURCE_TIMEZONE) => {
  const d = toDate(value);
  if (!d) return '';
  const { hour, minute } = partsInTz(d, tz, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${hour}:${minute}`;
};

/**
 * "YYYY-MM-DD HH:mm" in the resource timezone. Replaces the local
 * `formatBrusselsDateTime` previously embedded in SessionStatusChip.
 */
export const formatDateTimeInResourceTz = (value, tz = RESOURCE_TIMEZONE) => {
  const d = toDate(value);
  if (!d) return null;
  const p = partsInTz(d, tz, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Gate closure split into { date: "Oct 09", time: "03:00" } in the resource
 * timezone. Built from numeric parts because en-GB's short month varies by
 * runtime ("Sep" vs "Sept").
 */
export const formatGateClosureInResourceTz = (value, tz = RESOURCE_TIMEZONE) => {
  const d = toDate(value);
  if (!d) return null;
  const p = partsInTz(d, tz, {
    month: 'numeric',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { date: `${MONTHS[Number(p.month) - 1]} ${p.day}`, time: `${p.hour}:${p.minute}` };
};

/**
 * Short label for date-only strings (e.g. "Apr 15"). Used by the active
 * session chip for `target_day` values.
 */
export const formatTargetDay = (dateStr, tz = RESOURCE_TIMEZONE) => {
  if (!dateStr) return null;
  // Force UTC interpretation so a bare date doesn't shift across midnight.
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', timeZone: tz });
};

/**
 * Live timezone abbreviation for the resource (CET in winter, CEST in summer).
 * Falls back to the IANA name if the runtime can't supply a short token.
 */
export const getResourceTimezoneLabel = (tz = RESOURCE_TIMEZONE, at = new Date()) => {
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' });
    const part = fmt.formatToParts(at).find((p) => p.type === 'timeZoneName');
    return part?.value || tz;
  } catch {
    return tz;
  }
};

/**
 * Distinct resource-timezone labels across a set of instants, in first-seen
 * order (e.g. ['CEST', 'CET'] for an October month). A list of times can
 * straddle a DST switch, so one label taken at a single instant may be wrong
 * for the rest. Invalid/null values are skipped.
 */
export const getResourceTimezoneLabels = (values, tz = RESOURCE_TIMEZONE) => [
  ...new Set(
    (values || [])
      .map(toDate)
      .filter(Boolean)
      .map((d) => getResourceTimezoneLabel(tz, d))
  ),
];

/**
 * Timezone abbreviation of the viewer's browser at `at` (e.g. WEST, CET,
 * GMT-4). Suffix for timestamps rendered in local time, so they are never
 * mistaken for resource or UTC times.
 */
export const getLocalTimezoneLabel = (value = new Date()) => {
  const at = toDate(value) || new Date();
  // Explicit fallback: an undefined zone would otherwise hit the Brussels default.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  return getResourceTimezoneLabel(tz, at);
};

/**
 * UTC offset of `tz` at an instant, in minutes (Brussels: 60 in winter,
 * 120 in summer). Null for an invalid instant.
 */
export const getTimezoneOffsetMinutes = (tz, value) => {
  const d = toDate(value);
  if (!d) return null;
  const { timeZoneName } = partsInTz(d, tz, { timeZoneName: 'longOffset' });
  const match = /GMT([+-])(\d{2}):?(\d{2})?/.exec(timeZoneName || '');
  if (!match) return 0; // plain "GMT"
  const minutes = Number(match[2]) * 60 + Number(match[3] || 0);
  return match[1] === '-' ? -minutes : minutes;
};

/**
 * Wall-clock "YYYY-MM-DDTHH:mm" in `tz` (a datetime-local input value) to a
 * UTC Date. Null for an empty or malformed value.
 */
export const zonedWallTimeToUtc = (wall, tz = RESOURCE_TIMEZONE) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall || '');
  if (!match) return null;
  const [y, mo, d, h, mi] = match.slice(1).map(Number);
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  // The offset at the guess can differ from the one at the answer near a
  // DST switch, so correct once with the offset at the first estimate.
  const first = asUtc - getTimezoneOffsetMinutes(tz, new Date(asUtc)) * 60000;
  return new Date(asUtc - getTimezoneOffsetMinutes(tz, new Date(first)) * 60000);
};

/** UTC instant to a "YYYY-MM-DDTHH:mm" wall clock in `tz`, for datetime-local inputs. */
export const utcToZonedWallTime = (value, tz = RESOURCE_TIMEZONE) => {
  const formatted = formatDateTimeInResourceTz(value, tz);
  return formatted ? formatted.replace(' ', 'T') : '';
};

// ---------------------------------------------------------------------------
// Back-compat aliases — keep older imports working until call sites migrate.
// ---------------------------------------------------------------------------
export const formatBrusselsDateTime = (value) => formatDateTimeInResourceTz(value);

export const formatUtcDateTime = (value) => {
  const d = toDate(value);
  if (!d) return null;
  return d.toISOString().slice(0, 16).replace('T', ' ');
};
